import {
  Component,
  ElementRef,
  Output,
  ViewChild,
  EventEmitter,
  Input,
  signal,
} from '@angular/core';
import { UniSearchResponse } from 'src/app/interfaces/api';
import { UniService } from 'src/app/services/uni.service';
import { NgIf, NgFor } from '@angular/common';
import { University } from '../../models/university.model';
import { ErrorBlockComponent } from '../error-block/error-block.component';

@Component({
    selector: 'app-uni-lookup',
    templateUrl: './uni-lookup.component.html',
    styleUrls: ['./uni-lookup.component.scss'],
    imports: [
        NgIf,
        NgFor,
        ErrorBlockComponent,
    ],
})
export class UniLookupComponent {
  @ViewChild('lookup') public readonly lookup!: ElementRef;

  @Input() public noUnisFoundMessage: string | null = '';

  @Input()
  public set searchTerm(value: string | null) {
    this.searchTermState.set(value ?? '');
  }

  @Output() private readonly lookupComplete = new EventEmitter<number>();

  @Output() private readonly uniSelected = new EventEmitter<University>();

  @Output() private readonly alternativeUni = new EventEmitter<void>();

  @Output() private readonly addUni = new EventEmitter<string>();

  @Output() private readonly closeSelf = new EventEmitter<void>();

  public readonly isSearching = signal(false);

  public readonly apiResponse = signal<University[] | null>(null);

  public readonly searchTermState = signal('');

  private timeout!: NodeJS.Timeout;

  public readonly lookupError = signal(false);

  constructor(private readonly uniService: UniService) {}

  public onBlur(event: FocusEvent): void {
    if (event.relatedTarget) {
      this.closeSelf.emit();
    }
  }

  public giveFocus(): void {
    setTimeout(() => {
      const lookupElement = <HTMLInputElement>this.lookup.nativeElement;
      lookupElement.focus();
      lookupElement.select();
      if (this.searchTermState()) {
        this.lookupChange();
      }
    }, 0);
  }

  public lookupChange(): void {
    const lookupElement = <HTMLInputElement>this.lookup.nativeElement;

    clearTimeout(this.timeout);
    this.apiResponse.set(null);
    this.searchTermState.set(lookupElement.value);

    if (this.searchTermState() && this.searchTermState().length > 2) {
      this.timeout = setTimeout(() => {
        this.lookupError.set(false);
        this.isSearching.set(true);
        this.uniService.searchUniversities(this.searchTermState()).subscribe({
          next: (unis) => {
            this.isSearching.set(false);
            const uniResponse = <UniSearchResponse>unis;
            if (uniResponse.data) {
              this.apiResponse.set(uniResponse.data?.data ?? null);
              this.lookupComplete.emit(this.apiResponse()?.length ?? 0);
            } else {
              this.apiResponse.set(null);
              this.lookupComplete.emit(0);
            }
          },
          error: () => {
            this.lookupError.set(true);
            this.isSearching.set(false);
          },
        });
      }, 250);
    }
  }

  public selectUni(uni: University): void {
    this.searchTermState.set(uni.name);
    this.uniSelected.emit(uni);
  }

  public doAddUni(): void {
    this.addUni.emit(this.searchTermState());
  }

  public notAtUni(): void {
    this.alternativeUni.emit();
  }
}
